import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../Header';
import BackgroundImage from '../BackgroundImage';
import MobileBanner from '../MobileBanner';
import AnimatedPostButton from '../AnimatedPostButton';
import { AuthContext } from '../AuthContext';
import useDebounce from '../../hooks/useDebounce';

const MobileAppLayout = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isIndexPage = location.pathname === '/';
  const isPostCreatePage = location.pathname === '/post-create';
  const { isAuthenticated } = useContext(AuthContext);
  
  // Debounce search query with 300ms delay
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  // Track if this is the first render to avoid triggering search on mount
  const isFirstRender = useRef(true);
  // Track the last searched value to avoid duplicate searches
  const lastSearchedValue = useRef('');

  // Effect to handle debounced search with auto-reset
  useEffect(() => {
    // Skip the first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Only act if on IndexPage
    if (!isIndexPage) return;
    
    const trimmed = debouncedSearchQuery.trim();
    
    // Avoid duplicate searches for the same value
    if (trimmed === lastSearchedValue.current) return;
    
    if (trimmed.length === 0) {
      // Search was cleared - reset to all posts
      lastSearchedValue.current = '';
      if (window.indexPageOnReset) {
        window.indexPageOnReset();
      }
    } else if (trimmed.length >= 2) {
      // Minimum 2 characters to trigger search (reduces noise)
      lastSearchedValue.current = trimmed;
      if (window.indexPageOnSearch) {
        window.indexPageOnSearch(trimmed);
      }
    }
  }, [debouncedSearchQuery, isIndexPage]);

  const handleSearch = (query) => {
    // Manual submit - bypass debounce for immediate search
    const trimmed = query.trim();
    if (trimmed.length > 0 && isIndexPage && window.indexPageOnSearch) {
      lastSearchedValue.current = trimmed;
      window.indexPageOnSearch(trimmed);
    }
  };

  const handleReset = () => {
    // Clear search query
    setSearchQuery('');
    lastSearchedValue.current = '';
    
    // If on IndexPage, trigger the reset
    if (isIndexPage && window.indexPageOnReset) {
      window.indexPageOnReset();
    }
  };

  return (
    <div className={`mobile-layout mobile-app-shell ${!isAuthenticated && !isPostCreatePage ? 'has-bottom-button' : ''}`}>
      <BackgroundImage 
        alt="Background"
        className="bg-img"
      />
      <Header 
        onSearch={handleSearch} 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery}
        isSearchEnabled={isIndexPage}
        onReset={handleReset}
      />
      {/* Mobile promotional banner (bottom sheet) */}
      {/* Configure in: src/config/sideBannersConfig.js (mobileBannerConfig) */}
      <MobileBanner />
      <main className="mobile-main mobile-app-shell__content">
        {React.cloneElement(children, {
          searchQuery,
          setSearchQuery,
          onSearch: handleSearch
        })}
      </main>
      
      {/* Fixed bottom button for unauthorized users - hidden on post create page */}
      {/* {!isAuthenticated && !isPostCreatePage && (
        <AnimatedPostButton
          onClick={() => navigate('/post-create')}
          variant="mobile-fixed"
          text="Поделиться поездкой"
        />
      )} */}
    </div>
  );
};

export default MobileAppLayout;


